import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Calendar, Download, Edit2, Trash2, Plus, Filter, AlertCircle, CheckCircle, Loader, X, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const BillManagement = () => {
  const { api, user } = useAuth();
  const [bills, setBills] = useState([]);
  const [classes, setClasses] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStream, setSelectedStream] = useState('');
  const [selectedFeeStructure, setSelectedFeeStructure] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedBills, setSelectedBills] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [billsData, setBillsData] = useState({
    total_bills: 0,
    bills: [],
    class_name: '',
    stream_name: '',
    total_billed_amount: 0,
    total_paid_amount: 0,
    total_outstanding: 0,
    collection_rate: 0
  });

  const [generateForm, setGenerateForm] = useState({
    scope: 'class',
    class_id: '',
    fee_structure_id: '',
    due_date: new Date().toISOString().split('T')[0]
  });

  const [editForm, setEditForm] = useState({
    amount: 0
  });

  useEffect(() => {
    fetchClasses();
    fetchFeeStructures();
  }, []);

  useEffect(() => {
    if (selectedClass && selectedFeeStructure) {
      fetchBills();
    }
  }, [selectedClass, selectedStream, selectedFeeStructure, page]);

  useEffect(() => {
    // Update selectAll state based on selectedBills
    if (bills.length > 0) {
      setSelectAll(selectedBills.length === bills.length);
    }
  }, [selectedBills, bills]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const fetchClasses = async () => {
    try {
      const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
      setClasses(response.data);
      if (response.data.length > 0) {
        setSelectedClass(response.data[0].id);
      }
    } catch (error) {
      showMessage('error', 'Failed to fetch classes');
    }
  };

  const fetchFeeStructures = async () => {
    try {
      const response = await api.get(`/api/v1/fee-structures/school/${user.school_id}`);
      setFeeStructures(response.data);
      if (response.data.length > 0) {
        setSelectedFeeStructure(response.data[0].id);
      }
    } catch (error) {
      showMessage('error', 'Failed to fetch fee structures');
    }
  };

  const fetchBills = async () => {
    if (!selectedClass || !selectedFeeStructure) return;
    
    setLoading(true);
    try {
      let url = `/api/v1/bills/class/${selectedClass}?page=${page}&page_size=${pageSize}&fee_structure_id=${selectedFeeStructure}`;
      if (selectedStream) {
        url += `&stream_id=${selectedStream}`;
      }
      const response = await api.get(url);
      setBillsData(response.data);
      setBills(response.data.bills);
      setSelectedBills([]); // Clear selected bills when fetching new data
      setSelectAll(false);
    } catch (error) {
      showMessage('error', 'Failed to fetch bills');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBills = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        scope: generateForm.scope,
        fee_structure_id: parseInt(generateForm.fee_structure_id),
        due_date: generateForm.due_date
      };
      
      if (generateForm.scope === 'class') {
        payload.class_id = parseInt(generateForm.class_id);
      }

      await api.post('/api/v1/bills/generate', payload);
      showMessage('success', 'Bills generated successfully!');
      setShowGenerateModal(false);
      fetchBills();
      setGenerateForm({
        scope: 'class',
        class_id: '',
        fee_structure_id: '',
        due_date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to generate bills';
      showMessage('error', typeof errorMsg === 'string' ? errorMsg : errorMsg[0]?.msg || 'Failed to generate bills');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBill = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/api/v1/bills/${selectedBill.id}?amount=${parseFloat(editForm.amount)}`);
      showMessage('success', 'Bill updated successfully!');
      setShowEditModal(false);
      fetchBills();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update bill';
      showMessage('error', typeof errorMsg === 'string' ? errorMsg : errorMsg[0]?.msg || 'Failed to update bill');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBill = async () => {
    setLoading(true);
    try {
      await api.delete(`/api/v1/bills/${selectedBill.id}`);
      showMessage('success', 'Bill deleted successfully!');
      setShowDeleteModal(false);
      fetchBills();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete bill';
      showMessage('error', typeof errorMsg === 'string' ? errorMsg : errorMsg[0]?.msg || 'Failed to delete bill');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setLoading(true);
    try {
      // Delete each selected bill
      await Promise.all(
        selectedBills.map(billId => api.delete(`/api/v1/bills/${billId}`))
      );
      showMessage('success', `${selectedBills.length} bills deleted successfully!`);
      setShowBulkDeleteModal(false);
      setSelectedBills([]);
      setSelectAll(false);
      fetchBills();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete bills';
      showMessage('error', typeof errorMsg === 'string' ? errorMsg : errorMsg[0]?.msg || 'Failed to delete bills');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedBills(bills.map(bill => bill.id));
    } else {
      setSelectedBills([]);
    }
  };

  const handleSelectBill = (billId, checked) => {
    if (checked) {
      setSelectedBills([...selectedBills, billId]);
    } else {
      setSelectedBills(selectedBills.filter(id => id !== billId));
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const selectedClassData = classes.find(c => c.id === parseInt(selectedClass));
  const totalPages = Math.ceil(billsData.total_bills / pageSize);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Message Alert */}
      {message.text && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg transition-all ${
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Student Billing Management</h1>
            <p className="text-gray-600 mt-1">Generate and manage student bills</p>
          </div>
          <div className="flex items-center gap-3">
            {selectedBills.length > 0 && (
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 size={20} />
                Delete Selected ({selectedBills.length})
              </button>
            )}
            <button
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a3d3d] transition-colors"
            >
              <Plus size={20} />
              Generate Bills
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedStream('');
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
            >
              <option value="">Select Class</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Stream</label>
            <select
              value={selectedStream}
              onChange={(e) => {
                setSelectedStream(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
              disabled={!selectedClass}
            >
              <option value="">All Streams</option>
              {selectedClassData?.streams?.map(stream => (
                <option key={stream.id} value={stream.id}>{stream.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fee Structure</label>
            <select
              value={selectedFeeStructure}
              onChange={(e) => {
                setSelectedFeeStructure(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
            >
              <option value="">Select Fee Structure</option>
              {feeStructures.map(fs => (
                <option key={fs.id} value={fs.id}>{fs.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {selectedClass && selectedFeeStructure && !loading && bills.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Billed</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatCurrency(billsData.total_billed_amount)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="text-blue-600" size={24} />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Paid</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatCurrency(billsData.total_paid_amount)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Outstanding</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatCurrency(billsData.total_outstanding)}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="text-red-600" size={24} />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Collection Rate</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{billsData.collection_rate?.toFixed(1)}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Download className="text-purple-600" size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bills Table */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Bills</h2>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-[#0E5252]" size={32} />
            </div>
          ) : bills.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {!selectedClass || !selectedFeeStructure 
                  ? 'Please select a class and fee structure to view bills.'
                  : 'No bills found. Generate bills to get started.'
                }
              </p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-gray-300 text-[#0E5252] focus:ring-[#0E5252]"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedBills.includes(bill.id)}
                          onChange={(e) => handleSelectBill(bill.id, e.target.checked)}
                          className="rounded border-gray-300 text-[#0E5252] focus:ring-[#0E5252]"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{bill.invoice_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{bill.student_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(bill.total_amount)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(bill.due_date).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(bill.status)}`}>
                          {bill.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedBill(bill);
                              setEditForm({ amount: bill.total_amount });
                              setShowEditModal(true);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedBill(bill);
                              setShowDeleteModal(true);
                            }}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination */}
              <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, billsData.total_bills)} of {billsData.total_bills} results
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="px-3 py-1 text-sm">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Generate Bills Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Generate Bills</h3>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleGenerateBills}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Billing Scope</label>
                  <select
                    value={generateForm.scope}
                    onChange={(e) => setGenerateForm({ ...generateForm, scope: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                    required
                  >
                    <option value="class">Specific Class</option>
                    <option value="all">All Students</option>
                  </select>
                </div>
                
                {generateForm.scope === 'class' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
                    <select
                      value={generateForm.class_id}
                      onChange={(e) => setGenerateForm({ ...generateForm, class_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                      required
                    >
                      <option value="">Select Class</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fee Structure</label>
                  <select
                    value={generateForm.fee_structure_id}
                    onChange={(e) => setGenerateForm({ ...generateForm, fee_structure_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                    required
                  >
                    <option value="">Select Fee Structure</option>
                    {feeStructures.map(fs => (
                      <option key={fs.id} value={fs.id}>{fs.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={generateForm.due_date}
                    onChange={(e) => setGenerateForm({ ...generateForm, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                    required
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a3d3d] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating...' : 'Generate Bills'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Bill Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Edit Bill</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleEditBill}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                  required
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a3d3d] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Delete Bill</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this bill for {selectedBill?.student_name}? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBill}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div> 
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Delete Multiple Bills</h3>
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {selectedBills.length} selected bills? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting...' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillManagement;